import { debouncedMerge, memoizeAsync, MergedRequests, retryAsync } from "../../../Helpers/AsyncUtils";
import { ICandyApiClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { DraftFullId } from "../../../../../Common/CandyApiClient/Types/DraftFullId";
import {
    FocusServerErrorCode,
    GetByInnResponseInfo,
} from "../../../../../Common/CandyApiClient/Types/Focus/FocusTypes";

import { IFocusProvider } from "./IFocusProvider";

export class FocusProvider implements IFocusProvider {
    private static readonly failRequestPause = 30000;
    private readonly candyApi: ICandyApiClient;
    private readonly draftFullId: DraftFullId;
    private readonly getOrganizationName: (requestedPaths: string) => Promise<GetByInnResponseInfo>;

    public constructor(candyApi: ICandyApiClient, draftFullId: DraftFullId) {
        this.candyApi = candyApi;
        this.draftFullId = draftFullId;
        this.getOrganizationName = memoizeAsync(
            debouncedMerge(
                retryAsync(
                    (args: MergedRequests<string, GetByInnResponseInfo>) => this.requestToFocus(args),
                    FocusProvider.failRequestPause
                ),
                100,
                x => x
            ),
            x => x
        );
    }

    public async requestToFocus(requests: MergedRequests<string, GetByInnResponseInfo>): Promise<void> {
        const inns = requests.map(x => x.arg);
        const innInfos = await this.candyApi.getInfoByInns(this.draftFullId, inns);
        const failedPath = inns.find(inn => {
            const value = innInfos[inn];
            return (
                value != undefined &&
                (value.errorCode === FocusServerErrorCode.FocusError ||
                    value.errorCode === FocusServerErrorCode.HttpError)
            );
        });

        if (failedPath) {
            throw new Error(innInfos[failedPath]?.value ?? "RequestToFocusFailed");
        }
        for (const inn of inns) {
            const innInfo = innInfos[inn];
            if (innInfo != undefined) {
                requests.find(x => x.arg === inn)?.resolve(innInfo);
            }
        }
    }

    public async getOrganizationNameByInn(inn: string): Promise<GetByInnResponseInfo> {
        if (inn.length !== 10 && inn.length !== 12) {
            return {
                value: undefined,
                error: undefined,
                errorCode: FocusServerErrorCode.NotInn,
            };
        }

        try {
            return await this.getOrganizationName(inn);
        } catch {
            return {
                value: undefined,
                error: undefined,
                errorCode: FocusServerErrorCode.NotWork,
            };
        }
    }
}
